import type { AcessoDoUsuario } from '@/features/auth/perfil-de-acesso';
import { create } from 'zustand';
import { toast } from 'sonner';
import { refreshRequest } from '@/features/auth/api';
import { setAccessTokenGetter, setRefreshHandler } from '../lib/api-client';
import { clearPersistedQueryCache } from '../lib/query-cache';

export interface AuthUser {
  id: number;
  tenantId: number;
  email: string;
  name: string;
  profile: 'admin' | 'attendant';
  /** Departamentos vinculados ao user — usado pro controle de acesso
   *  (regra única: vê ticket sse `dept_id IS NULL` OU está em depts). */
  departmentIds: number[];
  /** Grupos de acesso do user. Usado p/ filtrar o alerta de canal
   *  desconectado (vê canal sse `default_access_group_id IS NULL` OU está no grupo). */
  accessGroupIds: number[];
  /** Telas/permissões do perfil de acesso (admin = tudo; null = só
   *  Atendimento). Vide features/auth/perfil-de-acesso.ts. */
  acesso: AcessoDoUsuario | null;
  /**
   * Presente quando sessão veio do fluxo de "support login" (super-admin
   * acessou via /auth/support/select-tenant). Frontend usa pra renderizar
   * banner amarelo no AppShell indicando sessão de suporte ativa.
   */
  impersonating?: { email: string };
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  /** True enquanto o bootstrapAuthLoop está retentando refresh em background.
   *  Router NÃO redireciona pra /login enquanto isso — user fica na URL atual
   *  com toast persistente "Sistema indisponível". */
  bootstrapping: boolean;
  setSession: (token: string, user: AuthUser) => void;
  clearSession: () => void;
  setAccessToken: (token: string) => void;
  setBootstrapping: (b: boolean) => void;
  /** Troca só o acesso (perfil mudou com a aba aberta — user.access_changed). */
  setAcesso: (acesso: AcessoDoUsuario | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  bootstrapping: false,
  setSession: (accessToken, user) => set({ accessToken, user, bootstrapping: false }),
  clearSession: () => set({ accessToken: null, user: null, bootstrapping: false }),
  setAccessToken: (accessToken) => set({ accessToken }),
  setBootstrapping: (b) => set({ bootstrapping: b }),
  setAcesso: (acesso) => set((s) => (s.user ? { user: { ...s.user, acesso } } : {})),
}));

// Conecta o api-client ao store pra anexar Bearer automaticamente
setAccessTokenGetter(() => useAuthStore.getState().accessToken);

// Auto-refresh em 401: token JWT expira em 15min. Quando uma request falha,
// `apiRequest` chama esse handler — ele troca o token via cookie HTTP-only
// de refresh, atualiza o store, e a request original é reexecutada.
// Em falha (refresh expirou também), limpa a sessão pra forçar redirect ao login.
setRefreshHandler(async () => {
  try {
    const result = await refreshRequest();
    useAuthStore.getState().setSession(result.accessToken, result.user);
    // Reconecta o Socket.IO com o token novo. Dynamic import pra evitar
    // ciclo de import (socket.ts → auth-store → socket.ts).
    try {
      const { refreshSocketAuth } = await import('@/features/atendimento/socket');
      refreshSocketAuth();
    } catch {
      // Se socket não foi inicializado ainda (rota fora do atendimento), no-op.
    }
    return true;
  } catch (err) {
    // Toast informativo: diferencia "sistema instável" (timeout) de "sessão
    // expirou" (refresh token revogado). Lição #80.
    const isTimeout = err instanceof Error && err.message.includes('timeout');
    if (isTimeout) {
      // Timeout: NÃO recarrega (bootstrapAuthLoop segue tentando). Zera o state
      // in-memory pra refletir "sem sessão" enquanto reconecta.
      useAuthStore.getState().clearSession();
      toast.error('Sistema temporariamente indisponível', {
        description: 'Estamos com instabilidade. Faça login novamente em instantes.',
        duration: 8000,
      });
    } else {
      toast.error('Sessão expirou', {
        description: 'Faça login novamente para continuar.',
        duration: 6000,
      });
      // 401-real (não-timeout): força reload total pra /login. window.location.href
      // descarta TUDO (Web Worker do socket, TanStack, persistQueryClient cache)
      // e recarrega limpo na rota /login.
      clearPersistedQueryCache();
      // Evita redirect-loop se já estiver em /login.
      if (!window.location.pathname.startsWith('/login')) {
        // NÃO chamar clearSession() antes do reload: ele já zera tudo, e mudar o
        // state aqui só dispararia um re-render com user=null que pisca o error
        // boundary do router ("Something went wrong!") por milissegundos.
        window.location.href = '/login';
      } else {
        // Já em /login e sem reload: zera o state in-memory mesmo assim.
        useAuthStore.getState().clearSession();
      }
    }
    return false;
  }
});
